import type { AgeBand, SafetyCategory, TutorSubject } from "@orbin/contracts";
import { SafetyVerdictSchema } from "@orbin/contracts";
import { parseKidsSubject } from "./subjects.js";

/**
 * The safety layer that runs on every child turn, before and after the model, whatever provider
 * answers. Meaning is judged by the LLM classifier against a versioned rubric. Structured things
 * (identifiers in text, provider and model names) are matched deterministically. A classifier that
 * fails, times out or answers outside the schema refuses the turn: an unjudged turn is not a pass.
 */

export const KIDS_SAFETY_RUBRIC_VERSION = "kids-safety-v5";

export interface SafetyVerdict {
  action: "allow" | "refuse" | "crisis";
  categories: SafetyCategory[];
  /** The school subject the classifier named, or null when unknown, missing or not a school subject. */
  subject: TutorSubject | null;
  /** True when the classifier could not judge the text. The turn is then refused. */
  unjudged: boolean;
}

export interface SafetyJudgeInput {
  rubricVersion: typeof KIDS_SAFETY_RUBRIC_VERSION;
  phase: "input" | "output";
  ageBand: AgeBand;
  text: string;
}

/** The provider-neutral classifier. The caller supplies the adapter; this module names no provider. */
export interface ChildSafetyJudge {
  judge(input: SafetyJudgeInput, signal: AbortSignal): Promise<unknown>;
}

export const CHILD_SAFETY_TIMEOUT_MS = 15000;

/** Judges one piece of child text. Anything other than a valid verdict in time becomes a refusal. */
export async function judgeChildText(
  judge: ChildSafetyJudge,
  input: { phase: "input" | "output"; ageBand: AgeBand; text: string },
): Promise<SafetyVerdict> {
  const signal = AbortSignal.timeout(CHILD_SAFETY_TIMEOUT_MS);
  try {
    const raw = await judge.judge({ rubricVersion: KIDS_SAFETY_RUBRIC_VERSION, ...input }, signal);
    const parsed = SafetyVerdictSchema.safeParse(raw);
    if (!parsed.success) return { action: "refuse", categories: [], subject: null, unjudged: true };
    // The subject is outside the schema check: a missing or unknown label becomes null and the safety decision stands.
    return { ...parsed.data, subject: parseKidsSubject((raw as { subject?: unknown }).subject), unjudged: false };
  } catch {
    return { action: "refuse", categories: [], subject: null, unjudged: true };
  }
}

export const CHILD_REFUSAL_TEXT =
  "Não posso ajudar com isso. Quer estudar outra coisa? Posso te ajudar com uma matéria ou com a lição.";

/** The refusal for a child asked to meet, send things to, or keep secrets from someone found online. */
export const CHILD_TRUSTED_ADULT_TEXT =
  "Isso não é seguro. Fale agora com um adulto de confiança, como alguém da sua família ou da escola, antes de fazer qualquer coisa com essa pessoa.";

/** Support for a child who may be at risk. Shown with the crisis action. */
export const CHILD_CRISIS_TEXT =
  "Sinto muito que você esteja passando por isso. Você não precisa lidar com isso sozinho. Fale agora com um adulto de confiança, como alguém da sua família ou da escola. Para conversar agora com alguém, ligue 188 (CVV, gratuito, 24 horas). Se alguém estiver machucando você, denuncie pelo Disque 100. Se estiver em perigo agora, ligue 190 (polícia) ou 192 (SAMU).";

/**
 * The reply when the classifier could not judge a child's message. A message that may be a
 * disclosure still gets the support lines, so the child is never left with a bare refusal.
 */
export const CHILD_UNJUDGED_TEXT =
  "Não posso ajudar com isso agora. Se você estiver passando por algo difícil, ligue 188 (CVV, gratuito, 24 horas). Se estiver em perigo agora, ligue 190 ou 192.";

/** Provider and model names the child must never see, matched as whole words. */
const PROVIDER_NAME_PATTERN =
  /\b(openai|anthropic|claude|chatgpt|gpt[-\w.]*|gemini|grok|deepseek|openrouter|mistral|llama|qwen|xai|orbin-main|headroom)\b/gi;

/** Removes provider and model names from child-facing text. */
export function redactProviderNames(text: string): { text: string; redacted: boolean } {
  let redacted = false;
  const out = text.replace(PROVIDER_NAME_PATTERN, () => {
    redacted = true;
    return "Preá";
  });
  return { text: out, redacted };
}

const CPF_PATTERN = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_PATTERN = /(?:\+?55\s?)?\(?\d{2}\)?\s?9?\d{4}[-\s]?\d{4}\b/g;

/** Whether the digits of a CPF string pass its two check digits. */
export function isValidCpf(value: string): boolean {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 11 || /^(\d)\1+$/.test(digits)) return false;
  for (const length of [9, 10]) {
    let sum = 0;
    for (let i = 0; i < length; i++) sum += Number(digits[i]) * (length + 1 - i);
    const check = ((sum * 10) % 11) % 10;
    if (check !== Number(digits[length])) return false;
  }
  return true;
}

export type PersonalDataKind = "cpf" | "email" | "phone";

/**
 * Masks identifiers before any provider sees the child's text, and reports which kinds were
 * found so the turn can raise an alert. Addresses and school names are left to the classifier.
 */
export function maskPersonalData(text: string): { text: string; found: PersonalDataKind[] } {
  const found: PersonalDataKind[] = [];
  let out = text.replace(CPF_PATTERN, (match) => {
    if (!isValidCpf(match)) return match;
    found.push("cpf");
    return "[dado pessoal]";
  });
  out = out.replace(EMAIL_PATTERN, () => {
    found.push("email");
    return "[dado pessoal]";
  });
  out = out.replace(PHONE_PATTERN, () => {
    found.push("phone");
    return "[dado pessoal]";
  });
  return { text: out, found: [...new Set(found)] };
}
